// Render da Home redesenhada (Mapiare).
//
// Estratégia: reusa a infra existente — `window.tripsApi` (bridge em
// features/home/tripsApi.js) pra carregar viagens, e os globais do
// static/js/home.js (`openWizard`, `openTrip`) pra ações. Esse módulo
// só desenha a UI nova (sidebar/bottom nav, header, hero, cards, busca)
// e categoriza/filtra com features/home/data.js.

import { waitForAuth } from '../../core/auth.js';
import { getUserSettings, resolveDisplayName, firstName } from '../../core/userSettings.js';
import { categorizeTrips, heroStatus, searchTrips, normalizeDate } from './data.js';
import { getTripImage, getTripImagesBatch, applyUnsplashCredit } from '../images/index.js';
import { wireCover, cacheCoverBlob, cachedCoverSrc, applyCoverPlaceholder, isOffline } from '../../core/imageCache.js';
import { APK_DOWNLOAD_URL, isWebBrowser } from '../../shared/apkDownload.js';
import { authFetch } from '../../core/auth.js';
import { API } from '../../core/api.js';
import { openNewTripModal } from './newTripModal.js';
import { openPartnersModal } from './partnersModal.js';
import { openDeleteTripModal } from './deleteTripModal.js';
import { renderAvatarHtml, avatarInitials as initialsOf, hydrateAvatarPhotos } from '../profile/avatar.js';
import { SIDEBAR_NAV_HTML, BOTTOM_NAV_HTML as navBottom, MORE_SHEET_HTML, wireMoreSheet } from '../../shared/nav.js';
import { messageModal } from '../../shared/components/messageModal.js';

const ICONS = {
  home:        '<path d="M3 12l9-9 9 9"/><path d="M5 10v10h14V10"/>',
  map:         '<path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z"/><path d="M9 3v15M15 6v15"/>',
  timeline:    '<circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/><path d="M12 7v10M8 12h8"/>',
  wallet:      '<path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5z"/><path d="M16 12h5"/><circle cx="17" cy="12" r="1" fill="currentColor"/>',
  doc:         '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/>',
  search:      '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>',
  bell:        '<path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/>',
  settings:    '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9 1.65 1.65 0 0 0 4.27 7.18l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.24.58.78 1 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  plus:        '<path d="M12 5v14M5 12h14"/>',
  arrowRight:  '<path d="M5 12h14M13 5l7 7-7 7"/>',
  chevronRight:'<path d="M9 6l6 6-6 6"/>',
  chevronDown: '<path d="M6 9l6 6 6-6"/>',
  clock:       '<circle cx="12" cy="9" r="7"/><path d="M12 5v4l3 2"/>',
  compass:     '<circle cx="12" cy="12" r="9"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" fill-opacity="0.12"/>',
  menu:        '<path d="M3 6h18M3 12h18M3 18h18"/>',
  user:        '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/>',
  logout:      '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/>',
  users:       '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  trash:       '<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>',
  exit:        '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/>',
  // Câmera — mesmo ícone usado no botão "Capa" do mapa (dayRail). Mantido
  // local pra não puxar dayRail (pesado) só por causa de um ícone.
  camera:      '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>',
};

const STATE = {
  trips: [],
  searchQuery: '',
  user: null,
  userSettings: null,
};

let _listenersAttached = false;

export async function mountHome(root) {
  // O #home-root já vem com o .app-loader (o "carinha andando") do HTML.
  // Mantemos esse loader visível até auth + settings + viagens TERMINAREM
  // de carregar — antes o shell substituía o loader na hora e a home abria
  // vazia, sem as viagens, enquanto o fetch ainda estava em andamento.
  STATE.user = await waitForAuth();

  // Settings e viagens carregam EM PARALELO — reduz o tempo de espera
  // (antes o settings era aguardado antes de começar a carregar as trips).
  const [userSettings, trips] = await Promise.all([
    getUserSettings().catch(() => null),
    loadTrips(),
  ]);
  STATE.userSettings = userSettings;
  STATE.trips = trips;

  // Tudo pronto → só agora trocamos o loader pela home completa.
  root.innerHTML = SHELL_HTML();
  attachShellListeners(root);
  wireMoreSheet(root);

  // Saudação personalizada + avatares
  setGreeting(root);
  refreshAvatars(root);

  // Render principal (hero + viagens)
  renderMain(root);

  // Imagens das viagens (assíncrono, sem bloquear)
  hydrateTripImages(root);
}

// ---------- shell + greeting ----------

function setGreeting(root) {
  const slot = root.querySelector('[data-slot="greeting"]');
  if (!slot) return;
  // Prefere users/{uid}.name (Settings) → fallback Auth.displayName → "Viajante"
  const full = resolveDisplayName(STATE.userSettings, STATE.user);
  slot.textContent = `Olá, ${firstName(full)}.`;
}

function avatarInitials(user) {
  const full = resolveDisplayName(STATE.userSettings, user);
  return initialsOf(full);
}

function avatarHtml(user, settings) {
  const full = resolveDisplayName(settings, user);
  return renderAvatarHtml({ uid: user?.uid, settings, name: full });
}

function refreshAvatars(root) {
  const html = avatarHtml(STATE.user, STATE.userSettings);
  root.querySelectorAll('[data-slot="avatar"], [data-slot="avatar-mini"]')
    .forEach((el) => { el.innerHTML = html; });
  hydrateAvatarPhotos(root);   // dispara load + seta background-image
}

// ---------- main render ----------

function renderMain(root) {
  const main = root.querySelector('[data-slot="main"]');
  if (!main) return;

  const filtered = STATE.searchQuery
    ? searchTrips(STATE.trips, STATE.searchQuery)
    : STATE.trips;

  // Em modo busca: SÓ mostra resultados (ou empty) — sem hero/seções
  if (STATE.searchQuery) {
    main.innerHTML = SEARCH_RESULTS_HTML(filtered);
    return;
  }

  const { hero, current, upcoming, past } = categorizeTrips(STATE.trips);
  // O hero mostra UMA viagem em andamento. Se houver mais de uma em curso, as
  // demais apareciam em lugar nenhum — agora vão numa seção "Em andamento".
  const otherCurrent = (current || []).filter((t) => t.id !== hero?.id);

  // Atalho "A seguir" no TOPO da Home: só quando a viagem do hero está EM
  // ANDAMENTO (hoje) e não está sendo gerada por IA. O conteúdo é hidratado
  // de forma assíncrona (carrega o itinerário do dia) — aqui só reservamos o
  // slot, que fica vazio até hydrateNextUp() popular.
  const heroIsCurrent = !!hero && (current || []).some((t) => t.id === hero.id) && !genStateOf(hero);

  main.innerHTML = `
    ${heroIsCurrent ? '<div data-slot="home-nextup"></div>' : ''}
    ${HERO_HTML(hero)}
    ${ONGOING_HTML(otherCurrent)}
    ${UPCOMING_HTML(upcoming)}
    ${PAST_HTML(past)}
  `;

  if (heroIsCurrent) hydrateNextUp(root, hero);
}

// Carrega o card "A seguir" no topo da Home (lazy: só importa/baixa o
// itinerário quando há viagem em curso). Degrada em silêncio se falhar.
async function hydrateNextUp(root, trip) {
  try {
    const mod = await import('./nextUpCard.js');
    await mod.hydrateHomeNextUp(root, trip);
  } catch (err) {
    console.warn('[home] next-up hydrate falhou:', err);
  }
}

// Seção com as outras viagens em andamento (além da que aparece no hero).
function ONGOING_HTML(trips) {
  if (!trips.length) return '';
  return `
    <section class="home-section">
      <header class="home-section-head">
        <h3 class="h3 home-section-title">Em andamento</h3>
        <span class="eyebrow home-section-meta">${trips.length} viage${trips.length === 1 ? 'm' : 'ns'} agora</span>
      </header>
      <div class="home-trip-grid">${trips.map(TRIP_CARD_HTML).join('')}</div>
    </section>
  `;
}

// ─── Geração de viagem com IA (cards "pendentes") ───────────────
//
// Uma viagem criada via "Gerar com IA" nasce com genStatus 'generating'
// (o backend a popula em background) e, no fim, vira 'ready' ou 'failed'.
// Enquanto não está 'ready', o card aparece cinza/desativado na home.

/** Estado da geração de uma viagem: 'generating' | 'failed' | null. */
function genStateOf(trip) {
  const s = trip?.genStatus;
  return (s === 'generating' || s === 'failed') ? s : null;
}

/** Badge sobreposto no card pra viagens ainda gerando / com falha. */
function genBadgeHtml(trip) {
  const st = genStateOf(trip);
  if (st === 'generating') {
    return `<span class="home-gen-badge home-gen-badge--loading">`
      + `<span class="home-gen-spinner" aria-hidden="true"></span>Gerando com IA…</span>`;
  }
  if (st === 'failed') {
    return '<span class="home-gen-badge home-gen-badge--failed">Falha na geração</span>';
  }
  return '';
}

// SVG path do ícone de IA (sparkles) — usado no modal de viagem em geração.
const AI_SPARKLES_PATH =
  '<path d="M12 3l1.6 4.8L18 9l-4.4 1.2L12 15l-1.6-4.8L6 9l4.4-1.2L12 3z"/>'
  + '<path d="M18 14l.9 2.7L21 18l-2.1.9L18 22l-.9-2.1L15 18l2.1-.9L18 14z"/>';

/**
 * Converte o genProgress { phase, done, total } numa fração 0..1 pra barra
 * de progresso do modal. O esqueleto pesa ~15%; os dias preenchem o resto.
 */
function genProgressFraction(pr) {
  const phase = pr?.phase || '';
  if (phase === 'saving') return 0.95;
  if (phase === 'days') {
    const total = Number(pr?.total) || 0;
    const done = Number(pr?.done) || 0;
    if (total > 0) return 0.15 + Math.min(1, done / total) * 0.78;
    return 0.20;
  }
  if (phase === 'skeleton') return 0.15;
  if (phase === 'skeleton-call' || phase === 'starting') return 0.06;
  return 0.04;
}

/**
 * Clique numa viagem ainda gerando: bate no endpoint de status e decide.
 *  - 'ready'      → abre a viagem.
 *  - 'generating' → avisa que ainda está sendo gerada (pode levar minutos).
 *  - 'failed'     → avisa o erro.
 * Em qualquer caso, atualiza o card com o status fresco.
 */
async function openOrAlertGeneratingTrip(tripId, root) {
  let data;
  try {
    const res = await authFetch(API.aiTripStatus(tripId));
    data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `o servidor respondeu ${res.status}`);
  } catch (err) {
    console.error('[home] checagem de status da viagem falhou:', err);
    await messageModal({
      tone: 'danger',
      title: 'Não consegui verificar',
      message: 'Não deu pra checar o status da viagem agora. '
        + 'Confira sua conexão e tente de novo em instantes.',
    });
    return;
  }

  // Reflete o status fresco no card (STATE.trips foi carregada no boot).
  const t = STATE.trips.find((x) => x.id === tripId);
  if (t && data.status) t.genStatus = data.status;

  if (data.status === 'ready') {
    location.href = '/visao-geral.html?trip=' + encodeURIComponent(tripId);
    return;
  }

  // Ainda não está pronta → atualiza o card e avisa num modal do app.
  renderMain(root);
  hydrateTripImages(root);

  if (data.status === 'failed' && data.errorKind === 'quota') {
    // Limite da IA atingido — temporário, não é erro de verdade. Aviso leve.
    await messageModal({
      tone: 'info',
      title: 'Limite da IA atingido',
      message: data.error
        || 'O limite de gerações de roteiro com IA foi atingido por enquanto. '
           + 'Tente de novo mais tarde.',
    });
  } else if (data.status === 'failed') {
    await messageModal({
      tone: 'danger',
      title: 'Falha na geração',
      message: 'A geração desta viagem com IA não deu certo.\n\n'
        + (data.error || 'Erro desconhecido.')
        + '\n\nVocê pode excluir a viagem e criar de novo.',
    });
  } else {
    // status 'generating' — mostra a FASE atual da geração. O backend grava
    // genProgress.phase em cada etapa; isso deixa claro até onde o job foi
    // (e, se travar, a fase para de avançar — é o sinal de diagnóstico).
    const pr = data.progress;
    const phase = pr?.phase || '';
    let detail;
    if (phase === 'days' && Number(pr?.total) > 0) {
      detail = `Detalhando os dias — ${Number(pr.done) || 0} de ${Number(pr.total)} prontos.`;
    } else if (phase === 'days') {
      detail = 'Detalhando os dias do roteiro…';
    } else if (phase === 'skeleton') {
      detail = 'Estrutura pronta — começando a detalhar os dias…';
    } else if (phase === 'skeleton-call') {
      detail = 'Montando a estrutura da viagem…';
    } else if (phase === 'saving') {
      detail = 'Quase lá — salvando o roteiro…';
    } else if (phase === 'starting') {
      detail = 'Preparando a geração…';
    } else {
      detail = 'A IA está montando o roteiro desta viagem.';
    }
    await messageModal({
      tone: 'info',
      icon: AI_SPARKLES_PATH,
      progress: genProgressFraction(pr),
      title: 'Viagem em geração',
      message: `${detail}\n\nIsso pode levar alguns minutos — abra de novo daqui a pouco.`,
    });
  }
}

function HERO_HTML(hero) {
  if (!hero) {
    return `
      <section class="home-hero home-hero--empty">
        <div class="home-hero-content">
          <span class="chip">Sem viagens</span>
          <h2 class="h2 home-hero-title">Que tal planejar a próxima?</h2>
          <p class="home-hero-sub">Crie sua viagem em segundos — datas, cidades, hotéis. O Mapiare cuida do resto.</p>
          <div class="home-hero-cta">
            <button class="btn btn-primary btn-lg" data-action="new-trip" type="button">
              ${icon('plus', 16)} Nova viagem
            </button>
          </div>
        </div>
      </section>
    `;
  }

  const status = heroStatus(hero);
  const dotClass = status?.kind === 'current' ? 'home-hero-dot--live' : '';
  const startIso = normalizeDate(hero.startDate, hero.year);
  const endIso   = normalizeDate(hero.endDate,   hero.year);
  const total = (startIso && endIso) ? daysBetween(startIso, endIso) + 1 : null;
  const dateLine = (startIso && endIso)
    ? `${fmtDate(startIso)} – ${fmtDate(endIso)}${total ? ` · ${total} dias` : ''}`
    : 'Sem datas definidas';

  // Mini-cards Mapa / Roteiro / Carteira / Configurações (só admin) —
  // visíveis em qualquer status (current/upcoming/past). Em viagem em
  // curso, label do roteiro vira "Roteiro de hoje".
  const isCurrent = status?.kind === 'current';
  const isAdmin = hero.userId === STATE.user?.uid;
  const cta = HERO_QUICK_CARDS_HTML(hero, { isCurrent, isAdmin });

  // Botão "Capa" — só admin pode trocar (mesma regra dos outros botões
  // admin do card). Click bubble pro handler que abre openTripCoverModal.
  const coverBtnHtml = isAdmin
    ? `<button type="button" class="home-hero-cover-btn"
         data-action="open-trip-cover-modal" data-trip-id="${esc(hero.id)}"
         aria-label="Trocar capa da viagem">
         ${icon('camera', 14)} <span>Capa</span>
       </button>`
    : '';

  // O card principal (hero) é clicável e abre o mapa — mesmo comportamento
  // dos cards de "Próximas" (.home-trip-cover com data-action="open-trip").
  // data-action no <section> + tabindex/role pra ativar via teclado (Enter).
  // O handler de 'open-trip' ignora cliques que nasceram em controles
  // internos (botão "Capa", quick-cards Roteiro/Carteira/Configurações),
  // então esses seguem com a ação própria sem disparar a navegação.
  return `
    <section class="home-hero${genStateOf(hero) ? ' is-gen' : ''}" data-trip-id="${esc(hero.id)}"
      data-action="open-trip" role="button" tabindex="0"
      aria-label="Abrir o mapa de ${esc(hero.name)}">
      <img class="home-hero-image" data-slot="hero-image" data-trip-name="${esc(hero.name)}" alt="" aria-hidden="true"/>
      <div class="home-hero-overlay" aria-hidden="true"></div>
      ${coverBtnHtml}

      <div class="home-hero-content">
        <span class="chip home-hero-chip">
          <span class="home-hero-dot ${dotClass}" aria-hidden="true"></span>
          ${esc(status?.text || '')}
        </span>
        ${genBadgeHtml(hero)}
        <h2 class="home-hero-title">${esc(hero.name)}</h2>
        <p class="home-hero-sub">${esc(dateLine)}</p>
        ${cta}
      </div>
    </section>
  `;
}

function HERO_QUICK_CARDS_HTML(hero, { isCurrent = false, isAdmin = false } = {}) {
  const tripQs = `?trip=${encodeURIComponent(hero.id)}`;
  const roteiroLabel = isCurrent ? 'Roteiro de hoje' : 'Roteiro';
  // NOTA: TODOS os quick-cards (Ver mapa/Roteiro/Carteira/Configurações)
  // usam navegação <a href> nativa — nenhum carrega `data-action="open-trip"`.
  // O handler genérico de open-trip roteia pra /visao-geral.html; se o card
  // "Ver mapa" tivesse esse data-action ele abriria a Visão geral em vez do
  // mapa (bug reportado). Sem data-action, o href="/mapa.html" navega direto
  // pro mapa, e o guard do handler do hero ignora cliques em controles
  // internos (não dispara a navegação pra Visão geral).
  return `
    <div class="home-hero-quick">
      <a class="home-hero-quick-card" href="/mapa.html${tripQs}" data-trip-id="${esc(hero.id)}">
        ${icon('map', 16)} <span>Ver mapa</span>
      </a>
      <a class="home-hero-quick-card" href="/roteiro.html${tripQs}" data-trip-id="${esc(hero.id)}">
        ${icon('timeline', 16)} <span>${esc(roteiroLabel)}</span>
      </a>
      <a class="home-hero-quick-card" href="/carteira.html${tripQs}" data-trip-id="${esc(hero.id)}">
        ${icon('wallet', 16)} <span>Carteira</span>
      </a>
      ${isAdmin ? `
        <a class="home-hero-quick-card" href="/trip-settings.html${tripQs}" data-action="trip-settings" data-trip-id="${esc(hero.id)}">
          ${icon('settings', 16)} <span>Configurações</span>
        </a>
      ` : ''}
    </div>
  `;
}

function UPCOMING_HTML(trips) {
  return `
    <section class="home-section">
      <header class="home-section-head">
        <h3 class="h3 home-section-title">Próximas</h3>
        <button class="btn btn-primary btn-sm" data-action="new-trip" type="button">
          ${icon('plus', 14)} Nova viagem
        </button>
      </header>
      ${trips.length
        ? `<div class="home-trip-grid">${trips.map(TRIP_CARD_HTML).join('')}</div>`
        : `<div class="home-empty">
             <p>Nenhuma viagem futura ainda.</p>
             <button class="btn btn-secondary btn-sm" data-action="new-trip" type="button">${icon('plus', 14)} Criar a primeira</button>
           </div>`}
    </section>
  `;
}

function PAST_HTML(trips) {
  if (!trips.length) return '';
  return `
    <section class="home-section">
      <header class="home-section-head">
        <h3 class="h3 home-section-title">Memórias</h3>
        <span class="eyebrow home-section-meta">${trips.length} viage${trips.length === 1 ? 'm' : 'ns'}</span>
      </header>
      <div class="home-past-grid">
        ${trips.map(PAST_CARD_HTML).join('')}
      </div>
    </section>
  `;
}

function TRIP_CARD_HTML(trip) {
  const status = heroStatus(trip, undefined, { short: true });
  const startIso = normalizeDate(trip.startDate, trip.year);
  const endIso   = normalizeDate(trip.endDate,   trip.year);
  const total = (startIso && endIso) ? daysBetween(startIso, endIso) + 1 : null;

  return `
    <article class="home-trip-card${genStateOf(trip) ? ' is-gen' : ''}" tabindex="0">
      <div class="home-trip-cover" data-action="open-trip" data-trip-id="${esc(trip.id)}">
        <img class="home-trip-image" data-slot="trip-image" data-trip-name="${esc(trip.name)}" alt="" aria-hidden="true"/>
        <div class="home-trip-cover-overlay" aria-hidden="true"></div>
        ${genBadgeHtml(trip) || (status ? `<span class="chip home-trip-chip">${icon('clock', 12)} ${esc(status.text)}</span>` : '')}
        <h4 class="home-trip-name">${esc(trip.name)}</h4>
      </div>
      <div class="home-trip-body">
        <div class="home-trip-meta">
          ${startIso ? `<span class="mono">${esc(fmtDate(startIso))}</span>` : '<span class="muted">Sem data</span>'}
          ${total ? `<span class="muted">· ${total} dias</span>` : ''}
        </div>
        <div class="home-trip-actions">
          ${trip.userId === STATE.user?.uid ? `
            <a href="/trip-settings.html?trip=${esc(trip.id)}" class="home-trip-action-btn"
              data-action="trip-settings" data-trip-id="${esc(trip.id)}"
              aria-label="Configurações da viagem" title="Configurações da viagem">
              ${icon('settings', 14)}
            </a>
          ` : ''}
          <button type="button" class="home-trip-action-btn" data-action="partners" data-trip-id="${esc(trip.id)}"
            aria-label="Parceiros" title="Parceiros">${icon('users', 14)}</button>
          ${trip.userId === STATE.user?.uid
            ? `<button type="button" class="home-trip-action-btn home-trip-action-btn--danger" data-action="delete-trip" data-trip-id="${esc(trip.id)}"
                aria-label="Excluir viagem" title="Excluir viagem">${icon('trash', 14)}</button>`
            : `<button type="button" class="home-trip-action-btn home-trip-action-btn--danger" data-action="delete-trip" data-trip-id="${esc(trip.id)}"
                aria-label="Sair da viagem" title="Sair da viagem">${icon('exit', 14)}</button>`}
        </div>
      </div>
    </article>
  `;
}

function PAST_CARD_HTML(trip) {
  const startIso = normalizeDate(trip.startDate, trip.year);
  const endIso   = normalizeDate(trip.endDate,   trip.year);
  const year = trip.year || (endIso ? endIso.slice(0, 4) : '');
  const total = (startIso && endIso) ? daysBetween(startIso, endIso) + 1 : null;
  return `
    <article class="home-past-card${genStateOf(trip) ? ' is-gen' : ''}" data-action="open-trip" data-trip-id="${esc(trip.id)}" tabindex="0">
      <img class="home-past-image" data-slot="trip-image" data-trip-name="${esc(trip.name)}" alt="" aria-hidden="true"/>
      <div class="home-past-overlay" aria-hidden="true"></div>
      <div class="home-past-content">
        ${genBadgeHtml(trip)}
        ${year ? `<span class="chip home-past-year">${esc(year)}</span>` : ''}
        <div class="home-past-bottom">
          <h4 class="home-past-name">${esc(trip.name)}</h4>
          ${total ? `<span class="home-past-meta mono">${total} dias</span>` : ''}
        </div>
      </div>
    </article>
  `;
}

function SEARCH_RESULTS_HTML(trips) {
  if (!trips.length) {
    return `
      <section class="home-search-empty">
        <h3 class="h3">Nada encontrado pra "${esc(STATE.searchQuery)}"</h3>
        <p>Crie uma nova viagem com esse nome ou ajuste a busca.</p>
        <button class="btn btn-primary btn-lg" data-action="new-trip" type="button">
          ${icon('plus', 16)} Nova viagem
        </button>
      </section>
    `;
  }
  return `
    <section class="home-section">
      <header class="home-section-head">
        <h3 class="h3 home-section-title">${trips.length} resultado${trips.length === 1 ? '' : 's'}</h3>
        <span class="eyebrow home-section-meta">para "${esc(STATE.searchQuery)}"</span>
      </header>
      <div class="home-trip-grid">
        ${trips.map(TRIP_CARD_HTML).join('')}
      </div>
    </section>
  `;
}

// ---------- imagens ----------

// Carrega imagens de cada card de viagem. `trip.cover` (capa custom escolhida
// via openTripCoverModal) tem PRIORIDADE sobre o auto-load via
// getTripImage(name) — pra TODOS os cards da mesma viagem (hero + trip card
// próxima + past card). Assim a mesma viagem aparece com a MESMA imagem
// em qualquer card. Fontes:
//   - source 'upload'    → fetch authenticated /api/documents/files → blob URL
//   - source 'unsplash'  → usa cover.url direto + crédito renderizado
// Sem cover → cai no batch Unsplash do nome.
//
// Trip lookup: cada img tem `data-trip-name`; o `data-trip-id` mora num
// ancestor (.home-hero, .home-trip-cover, .home-past-card). `closest()`
// resolve em todos os casos.
async function hydrateTripImages(root) {
  const imgs = Array.from(root.querySelectorAll('img[data-slot$="-image"], img[data-slot="trip-image"], img[data-slot="hero-image"]'));

  // Particiona: imgs que têm trip.cover custom (resolve direto, sem Unsplash)
  // vs imgs que precisam do batch Unsplash.
  const pending = [];
  const resolved = new Set();

  for (const img of imgs) {
    const tripId = img.closest('[data-trip-id]')?.dataset.tripId;
    const trip = tripId ? STATE.trips.find((t) => t.id === tripId) : null;
    const cover = trip?.cover;
    if (!cover?.url) { pending.push(img); continue; }

    const name = img.dataset.tripName || '';
    if (cover.source === 'upload') {
      // Offline: capa de upload (R2 auth-gated) não é alcançável — lê do cache
      // local; sem cache, placeholder (não vai pro batch, que também é online).
      if (isOffline()) {
        img.alt = name;
        const local = await cachedCoverSrc(cover.url, tripId);
        if (local) {
          img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
          img.src = local;
        } else {
          applyCoverPlaceholder(img);
        }
        applyUnsplashCredit(img.parentElement, null);
        resolved.add(img);
        continue;
      }
      // R2 é auth-gated; precisa fetch + blob URL.
      try {
        const res = await authFetch(cover.url);
        if (!res.ok) { pending.push(img); continue; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        img.alt = name;
        img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
        img.src = url;
        cacheCoverBlob(cover.url, tripId, blob); // persiste a capa pra uso offline
        // Upload → sem crédito Unsplash (foto autoral do user).
        applyUnsplashCredit(img.parentElement, null);
        resolved.add(img);
      } catch (err) {
        console.warn('[home] cover upload load failed:', err.message);
        pending.push(img);
      }
    } else {
      // source==='unsplash' (ou direta http). Renderiza direto.
      img.alt = name;
      img.dataset.unsplashPhotographer = cover.photographer || '';
      img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
      img.src = cover.url;
      wireCover(img, cover.url, tripId, {}); // cacheia online; offline → cache/placeholder
      if (cover.source === 'unsplash' && cover.photographer) {
        applyUnsplashCredit(img.parentElement, {
          photographer: cover.photographer,
          photographerUrl: cover.photographerUrl,
        });
      } else {
        applyUnsplashCredit(img.parentElement, null);
      }
      resolved.add(img);
    }
  }

  // Batch Unsplash pros imgs sem cover custom (ou cujo upload falhou).
  const titles = pending.map((img) => img.dataset.tripName).filter(Boolean);
  if (!titles.length) return;

  // Offline: o batch Unsplash precisa de rede. Sem imagem nem cache pra esses
  // (nunca tiveram URL) → placeholder, nunca imagem quebrada.
  if (isOffline()) { pending.forEach(applyCoverPlaceholder); return; }

  const map = await getTripImagesBatch(titles);
  for (const img of pending) {
    const name = img.dataset.tripName;
    const photo = name ? map.get(name) : null;
    if (!photo) continue;
    img.alt = photo.altDescription || photo.description || name || '';
    img.dataset.unsplashPhotographer = photo.photographer || '';
    img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
    const photoUrl = photo.url || photo.urlSmall || '';
    img.src = photoUrl;
    wireCover(img, photoUrl, img.closest('[data-trip-id]')?.dataset.tripId || name, {}); // cache offline
    // Caption de crédito Unsplash (parent: .home-hero / .home-trip-cover /
    // .home-past-card — todos têm position:relative). Cria/reusa um
    // <span class="unsplash-credit"> no canto inferior; CSS em home.css.
    applyUnsplashCredit(img.parentElement, photo);
  }
}

// ---------- shell HTML ----------

function SHELL_HTML() {
  return `
    <div class="home-shell">
      ${SIDEBAR_HTML()}

      <div class="home-stage">
        <div class="home-page-body">
          <header class="home-greeting">
            <!-- Linha superior: brand (mobile-only) à esquerda + ações à direita.
                 Em desktop, a brand fica oculta via CSS porque já vive na sidebar. -->
            <div class="home-greeting-top">
              <a class="home-greeting-brand" href="/" aria-label="Mapiare">
                <img src="/img/brand/logo-mark.png"     alt="" class="home-greeting-mark" aria-hidden="true"/>
                <img src="/img/brand/logo-wordmark.png" alt="Mapiare" class="home-greeting-wordmark"/>
              </a>
              <div class="home-greeting-actions">
                <button class="btn btn-ghost btn-icon" data-action="search-toggle" aria-label="Buscar" type="button">${icon('search', 18)}</button>
                <button class="btn btn-ghost btn-icon home-bell" data-action="bell" aria-label="Notificações" type="button">${icon('bell', 18)}<span class="notif-badge" data-notif-badge hidden></span></button>
                ${isWebBrowser() ? `<a class="btn btn-ghost btn-icon" href="${APK_DOWNLOAD_URL}" aria-label="Baixar app Android" title="Baixar app Android"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/></svg></a>` : ''}
                <a class="btn btn-ghost btn-icon home-greeting-settings" href="/settings.html" aria-label="Configurações">${icon('settings', 18)}</a>
                <button class="avatar home-avatar" data-action="profile-menu" aria-label="Perfil"
                  aria-haspopup="menu" type="button" data-slot="avatar">M</button>
              </div>
            </div>
            <!-- Linha inferior: eyebrow + saudação -->
            <div class="home-greeting-main">
              <p class="eyebrow" data-slot="today"></p>
              <h1 class="h1 home-greeting-title" data-slot="greeting">Olá.</h1>
            </div>
          </header>

          <div class="home-search" data-slot="search-bar" hidden>
            <span class="home-search-icon">${icon('search', 16)}</span>
            <input type="search" class="home-search-input" data-slot="search-input"
              placeholder="Buscar viagem, cidade, hotel…" autocomplete="off"/>
            <button class="btn btn-ghost btn-sm" data-action="search-clear" type="button">Limpar</button>
          </div>

          <div data-slot="main"></div>
        </div>
      </div>

      ${BOTTOM_NAV_HTML()}
    </div>
  `;
}

function SIDEBAR_HTML() {
  return `
    <aside class="home-sidebar" data-slot="sidebar">
      <div class="home-sidebar-head">
        <button class="home-sidebar-toggle" data-action="sidebar-toggle"
          aria-label="Recolher menu" title="Recolher menu" type="button">
          ${icon('menu', 18)}
        </button>
        <a href="/" class="home-sidebar-brand-link" aria-label="Mapiare">
          <img src="/img/brand/logo-mark.png"     alt="" class="home-sidebar-mark" aria-hidden="true"/>
          <img src="/img/brand/logo-wordmark.png" alt="Mapiare" class="home-sidebar-wordmark"/>
        </a>
      </div>

      ${SIDEBAR_NAV_HTML('home')}

      <div class="home-sidebar-footer">
        <a class="sidebar-item" href="/settings.html" data-nav="settings" title="Configurações">
          ${icon('settings', 18)} <span class="home-sidebar-label">Configurações</span>
        </a>
        <a class="sidebar-item home-sidebar-profile" href="/profile.html" data-nav="profile" title="Perfil">
          <span class="avatar avatar-sm" data-slot="avatar-mini">M</span>
          <span class="home-sidebar-label" data-slot="profile-label">Perfil</span>
        </a>
      </div>
    </aside>
  `;
}

function TOPBAR_HTML() {
  // Mobile-only topbar — slim, só com a logo. Ações (sino/configs/avatar)
  // ficam no greeting-actions logo abaixo, evitando duplicação.
  return `
    <header class="home-topbar" role="banner">
      <a class="home-topbar-brand" href="/" aria-label="Mapiare">
        <img src="/img/brand/logo-mark.png"     alt="" class="home-topbar-mark" aria-hidden="true"/>
        <img src="/img/brand/logo-wordmark.png" alt="Mapiare" class="home-topbar-wordmark"/>
      </a>
    </header>
  `;
}

function BOTTOM_NAV_HTML() {
  return navBottom('home') + MORE_SHEET_HTML();
}

// ---------- listeners ----------

function attachShellListeners(root) {
  if (_listenersAttached) return;
  _listenersAttached = true;

  // Today eyebrow
  const today = root.querySelector('[data-slot="today"]');
  if (today) today.textContent = formatTodayPt();

  // Avatar é atualizado por refreshAvatars() depois que mountHome carrega
  // settings — sem isso, o setTimeout(0) rodava ANTES do `await getUserSettings()`
  // resolver e a foto não aparecia (pegava só iniciais).

  // Delegação de cliques pra ações
  root.addEventListener('click', async (e) => {
    const target = e.target.closest('[data-action]');
    if (!target) return;
    const action = target.dataset.action;

    if (action === 'new-trip') {
      e.preventDefault();
      try {
        const trip = await openNewTripModal({
          defaultCurrency: STATE.userSettings?.primaryCurrency || 'BRL',
        });
        if (trip?.id) {
          // Viagem "Gerar com IA": NÃO abre (ainda está vazia, gerando em
          // background) — recarrega a home pra mostrar o card cinza.
          if (genStateOf(trip)) {
            STATE.trips = await loadTrips();
            renderMain(root);
            hydrateTripImages(root);
          } else if (typeof window.openTrip === 'function') {
            location.href = '/visao-geral.html?trip=' + encodeURIComponent(trip.id);
          }
        }
      } catch (err) {
        console.error('[home] new-trip failed:', err);
      }
      return;
    }

    if (action === 'open-trip') {
      // Guard: o hero (card principal) carrega data-action="open-trip" no
      // <section> inteiro, mas tem controles internos — botão "Capa" e os
      // quick-cards Roteiro/Carteira/Configurações. Cliques nesses controles
      // NÃO devem abrir o mapa. Se o clique nasceu num <a>/<button> interno
      // que não é o próprio target de open-trip, ignora aqui e deixa o
      // controle fazer sua ação (handler dedicado ou navegação nativa).
      const innerControl = e.target.closest('a, button');
      if (innerControl && innerControl !== target && target.contains(innerControl)) {
        return;
      }
      e.preventDefault();
      const id = target.dataset.tripId;
      if (!id) return;
      // Viagem ainda gerando (ou com falha): não abre direto — checa o
      // status no backend e só abre se já estiver pronta.
      const trip = STATE.trips.find((t) => t.id === id);
      if (genStateOf(trip)) {
        openOrAlertGeneratingTrip(id, root);
        return;
      }
      location.href = '/visao-geral.html?trip=' + encodeURIComponent(id);
      return;
    }

    if (action === 'partners') {
      e.preventDefault();
      e.stopPropagation();
      const id = target.dataset.tripId;
      const trip = STATE.trips.find((t) => t.id === id);
      if (!trip) return;
      try {
        await openPartnersModal(trip);
        // Recarrega trips pra refletir membros/invites atualizados
        STATE.trips = await loadTrips();
        renderMain(root);
        hydrateTripImages(root);
      } catch (err) {
        console.error('[home] partners failed:', err);
      }
      return;
    }

    if (action === 'delete-trip') {
      e.preventDefault();
      e.stopPropagation();
      const id = target.dataset.tripId;
      const trip = STATE.trips.find((t) => t.id === id);
      if (!trip) return;
      const result = await openDeleteTripModal(trip);
      if (result?.kind === 'deleted' || result?.kind === 'left') {
        STATE.trips = await loadTrips();
        renderMain(root);
        hydrateTripImages(root);
      }
      return;
    }

    // Capa da viagem (hero) — abre o openTripCoverModal e, no save, atualiza
    // trip.cover localmente + re-hidrata só o hero (não força um full reload
    // pra evitar perder posição/scroll).
    if (action === 'open-trip-cover-modal') {
      e.preventDefault();
      e.stopPropagation();
      const id = target.dataset.tripId;
      const trip = STATE.trips.find((t) => t.id === id);
      if (!trip) return;
      try {
        const mod = await import('../images/coverImageModal.js');
        mod.openTripCoverModal({
          trip,
          currentCover: trip.cover || null,
          onSaved: (cover) => {
            // Atualiza STATE in-place — próximo render já vê a nova capa.
            trip.cover = cover || null;
            // Re-hidrata a hero image (e só ela): hydrateTripImages é
            // idempotente, mas pra evitar reload de todos os cards,
            // chamamos com escopo do hero.
            const heroSection = root.querySelector('.home-hero');
            if (heroSection) hydrateTripImages(heroSection);
          },
        });
      } catch (err) {
        console.error('[home] open trip cover modal failed:', err);
      }
      return;
    }

    if (action === 'bell') {
      e.preventDefault();
      window.openNotifications?.();
      return;
    }

    if (action === 'search-toggle') {
      e.preventDefault();
      const bar = root.querySelector('[data-slot="search-bar"]');
      const input = root.querySelector('[data-slot="search-input"]');
      if (!bar) return;
      const willShow = bar.hasAttribute('hidden');
      bar.toggleAttribute('hidden', !willShow);
      if (willShow && input) setTimeout(() => input.focus(), 50);
      return;
    }

    if (action === 'search-clear') {
      e.preventDefault();
      const input = root.querySelector('[data-slot="search-input"]');
      if (input) { input.value = ''; STATE.searchQuery = ''; renderMain(root); hydrateTripImages(root); }
      return;
    }

    if (action === 'sidebar-toggle') {
      e.preventDefault();
      const sidebar = root.querySelector('[data-slot="sidebar"]');
      if (!sidebar) return;
      const willCollapse = !sidebar.classList.contains('is-collapsed');
      sidebar.classList.toggle('is-collapsed', willCollapse);
      try { localStorage.setItem('home-sidebar-collapsed', willCollapse ? '1' : '0'); } catch {}
      target.setAttribute('aria-label', willCollapse ? 'Expandir menu' : 'Recolher menu');
      target.setAttribute('title', willCollapse ? 'Expandir menu' : 'Recolher menu');
      return;
    }

    if (action === 'profile-menu') {
      e.preventDefault();
      e.stopPropagation();
      toggleProfileMenu(root, target);
      return;
    }

    if (action === 'logout') {
      e.preventDefault();
      closeProfileMenu(root);
      if (typeof window.logoutConfirm === 'function') window.logoutConfirm();
      else location.href = '/login.html';
      return;
    }
  });

  // Fecha menu ao clicar fora
  document.addEventListener('click', () => closeProfileMenu(root));

  // Restaura estado colapsado da sidebar
  try {
    if (localStorage.getItem('home-sidebar-collapsed') === '1') {
      const sb = root.querySelector('[data-slot="sidebar"]');
      sb?.classList.add('is-collapsed');
      const tgl = root.querySelector('[data-action="sidebar-toggle"]');
      if (tgl) {
        tgl.setAttribute('aria-label', 'Expandir menu');
        tgl.setAttribute('title', 'Expandir menu');
      }
    }
  } catch {}

  // Busca em tempo real
  root.addEventListener('input', (e) => {
    if (e.target.matches('[data-slot="search-input"]')) {
      STATE.searchQuery = e.target.value;
      renderMain(root);
      hydrateTripImages(root);
    }
  });

  // Esc fecha busca
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && e.target.matches('[data-slot="search-input"]')) {
      e.target.value = '';
      STATE.searchQuery = '';
      const bar = root.querySelector('[data-slot="search-bar"]');
      bar?.setAttribute('hidden', '');
      renderMain(root);
      hydrateTripImages(root);
    }
    // Enter em card abre trip. Guard: o hero tem controles internos
    // (quick-cards <a>) que já são ativáveis por Enter nativamente — não
    // deixamos o Enter num desses borbulhar e abrir o mapa.
    if (e.key === 'Enter') {
      const card = e.target.closest('[data-action="open-trip"]');
      const innerControl = e.target.closest('a, button');
      const isInnerControl = card && innerControl && innerControl !== card
        && card.contains(innerControl);
      if (card && !isInnerControl) {
        e.preventDefault();
        const id = card.dataset.tripId;
        if (!id) return;
        const trip = STATE.trips.find((t) => t.id === id);
        if (genStateOf(trip)) {
          openOrAlertGeneratingTrip(id, root);
        } else if (typeof window.openTrip === 'function') {
          location.href = '/visao-geral.html?trip=' + encodeURIComponent(id);
        }
      }
    }
  });
}

// ---------- profile dropdown menu ----------

const PROFILE_MENU_ID = 'home-profile-menu';

function toggleProfileMenu(root, anchor) {
  const existing = document.getElementById(PROFILE_MENU_ID);
  if (existing) {
    closeProfileMenu(root);
    return;
  }
  showProfileMenu(root, anchor);
}

function closeProfileMenu() {
  const el = document.getElementById(PROFILE_MENU_ID);
  if (el) el.remove();
}

function showProfileMenu(root, anchor) {
  const menu = document.createElement('div');
  menu.id = PROFILE_MENU_ID;
  menu.className = 'home-profile-menu';
  menu.setAttribute('role', 'menu');
  menu.innerHTML = `
    <a class="home-profile-menu-item" href="/profile.html" role="menuitem">
      <span class="home-profile-menu-icon">${icon('user', 16)}</span>
      <span>Detalhes do perfil</span>
    </a>
    <button class="home-profile-menu-item home-profile-menu-item--destructive"
      data-action="logout" role="menuitem" type="button">
      <span class="home-profile-menu-icon">${icon('logout', 16)}</span>
      <span>Sair da conta</span>
    </button>
  `;

  // Posiciona o menu logo abaixo+direita do anchor (avatar)
  const rect = anchor.getBoundingClientRect();
  const top = rect.bottom + 8 + window.scrollY;
  const right = window.innerWidth - rect.right - window.scrollX;
  menu.style.top = `${top}px`;
  menu.style.right = `${right}px`;

  document.body.appendChild(menu);

  // Wire o logout DIRETAMENTE no menu — antes o clique borbulhava esperando
  // chegar no listener do root, mas o menu vive em document.body (fora do
  // root), e havia stopPropagation no clique do menu. Resultado: o handler
  // de logout nunca disparava ("clico em Sair e nada acontece"). Bind local
  // resolve sem depender de bubble.
  const logoutBtn = menu.querySelector('[data-action="logout"]');
  logoutBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    closeProfileMenu();
    if (typeof window.logoutConfirm === 'function') window.logoutConfirm();
    else location.href = '/login.html';
  });

  // Fecha ao clicar fora do menu (e fora do avatar/anchor que abriu).
  // Usa setTimeout pra evitar fechar imediatamente no mesmo clique que abriu.
  setTimeout(() => {
    document.addEventListener('click', function outsideClick(e) {
      if (menu.contains(e.target) || anchor.contains(e.target)) return;
      document.removeEventListener('click', outsideClick);
      closeProfileMenu();
    });
  }, 0);

  // Esc fecha
  document.addEventListener('keydown', function k(e) {
    if (e.key === 'Escape') {
      document.removeEventListener('keydown', k);
      closeProfileMenu();
    }
  });
}

// ---------- carregamento de viagens ----------

async function loadTrips() {
  // Espera o bridge `window.tripsApi` (carregado por src/features/home/tripsApi.js)
  if (window.tripsApiReady) {
    try { await window.tripsApiReady; } catch {}
  }
  if (!window.tripsApi) return [];
  try {
    return await window.tripsApi.list();
  } catch (e) {
    console.warn('[home] failed to load trips:', e);
    return [];
  }
}

// ---------- helpers ----------

function icon(name, size = 18, extraCls = '') {
  const paths = ICONS[name];
  if (!paths) return '';
  return `<svg class="ic ${extraCls}" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
}

function daysBetween(startIso, endIso) {
  const a = new Date(startIso + 'T00:00:00Z').getTime();
  const b = new Date(endIso + 'T00:00:00Z').getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86400000));
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function formatTodayPt() {
  const d = new Date();
  const w = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.getDay()];
  const m = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][d.getMonth()];
  return `${w}, ${d.getDate()} ${m}`;
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
